import { useEffect, useRef } from "react";
import { apiService } from "@/services";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import queryClient from "@/query-client";
import type { GetKeywordBankRunResponse } from "@/types";
import { isRunInFlight, RUN_POLL_INTERVAL_MS } from "@/utils/landscapeRunStatus";
import { getKeywordsQueryPrefix } from "@/resources/keyword/keyword.api";

/**
 * `GET /api/products/hero/{id}/keyword-bank-run`
 * (`components/work-run-log.md § GET /api/products/hero/{id}/keyword-bank-run`,
 * `WEB-003`). The contract mirror lives in `src/types/keyword-bank-run.ts` --
 * this module imports it and declares no shape of its own
 * (`conventions/08-api-contracts.md`). Poll interval and the
 * queued/running/stopped predicate come from the shared work-run vocabulary
 * (`src/utils/landscapeRunStatus.ts`) rather than a second copy.
 */

// Distinct from `getKeywordsQueryPrefix` (`["product", "keywords", …]`,
// `src/resources/keyword/keyword.api.ts`) so invalidating the keyword list
// never refetches the run, and invalidating the run never refetches the
// keyword list.
const keywordBankRunQueryKeyPrefix = ["product", "keyword-bank-run"] as const;

export const keywordBankRunQueryKey = (productId: number) =>
  [...keywordBankRunQueryKeyPrefix, String(productId)] as const;

// Below RUN_POLL_INTERVAL_MS, the same margin `landscape-log.api.ts` uses --
// otherwise a background focus/reconnect refetch could be suppressed as
// "still fresh" for up to the poll interval, which would defeat FR-7's
// "sees the state change without pressing anything".
const KEYWORD_BANK_RUN_STALE_TIME_MS = 10_000;

/**
 * The poll predicate (FR-7, NFR-2): poll while the loaded run's reported
 * status is `queued`, `running` or `stopped`; stop on every ending and when
 * there is no run at all. A separate exported function so it is directly
 * unit-testable without inspecting the registered `refetchInterval`
 * callback.
 *
 * `data.run === null` is its own branch, not folded into the `!data`
 * check above it: every hero product added before this ships has no run
 * (FR-9), so a predicate that polled on `null` would poll forever, on the
 * majority of products, for a row that will never exist.
 */
export function computeKeywordBankRunRefetchInterval(
  data: GetKeywordBankRunResponse | undefined,
): number | false {
  if (!data) return false;
  if (data.run === null) return false;
  return isRunInFlight(data.run.status) ? RUN_POLL_INTERVAL_MS : false;
}

/**
 * `useGetKeywordBankRun(productId)` -- the React Query result, unchanged.
 * `data` is `GetKeywordBankRunResponse | undefined`, so a consumer's
 * `data?.run` is `KeywordBankRun | null | undefined`: *not loaded / errored*
 * versus *never built* are two different things (`WEB-004` branches on
 * both), and this hook does not collapse them -- it does not normalise
 * `undefined` to `null`, does not return a bare `run`, and does not swallow
 * `isError` into a `null` run.
 *
 * `apiService` turns a non-401 failure into a thrown `ApiError`, and
 * `query-client.ts` retries only `isNetworkError || status >= 500`. That is
 * exactly why the endpoint answers `200 {"run": null}` instead of `404`;
 * this hook adds no error branch that would re-create the failure mode the
 * endpoint was shaped to avoid.
 *
 * Sends no `brand_id` -- a keyword bank run is scoped by product id alone,
 * the same as `getKeywordsQueryPrefix`.
 *
 * `options.enabled` (default `true`, so existing callers are unaffected) lets
 * a caller suppress the request entirely (AUT-196 PR review): the public
 * search-term share view has no product id and must not fire
 * `GET /products/hero/0/keyword-bank-run`, an `auth:api` route whose 401
 * bounces every anonymous visitor to `/login`. When disabled the query does
 * not run and the poll stays dormant; re-enabling it resumes both.
 *
 * Owns one side effect: when the polled run transitions from a reported
 * in-flight status (`queued`/`running`/`stopped`) to any of the three
 * endings, it invalidates the `["product", "keywords", …]` prefix exactly
 * once for that transition -- required, not tidy, because
 * `succeeded_without_keywords` and `failed` raise no broadcast event at all,
 * and a `succeeded_with_keywords` run's own `KeywordBankEnriched` event only
 * covers the enrichment-success path (see `components/keyword.md` FR-4). A
 * `sawInFlight` ref, not a comparison against the previous poll's status,
 * is what keeps this from firing on `queued → running`, on every poll of a
 * run that stays `running`, or on a first response that already carries an
 * ending for a run this client never saw in flight.
 */
export function useGetKeywordBankRun(
  productId: number,
  options?: { enabled?: boolean },
): UseQueryResult<GetKeywordBankRunResponse> {
  const enabled = options?.enabled ?? true;

  const query = useQuery<GetKeywordBankRunResponse>({
    queryKey: keywordBankRunQueryKey(productId),
    queryFn: () => apiService.get<GetKeywordBankRunResponse, undefined>(`/products/hero/${productId}/keyword-bank-run`),
    enabled,
    staleTime: KEYWORD_BANK_RUN_STALE_TIME_MS,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    // Reads `query.state.data`, which TanStack v5 keeps populated with the
    // last successfully loaded value across a failed background refetch --
    // so a refresh that errors neither blanks the banner nor stops the poll
    // it was itself supposed to keep alive.
    refetchInterval: (q) => computeKeywordBankRunRefetchInterval(q.state.data),
    // refetchIntervalInBackground left at the React Query default (false):
    // a page holding one of the permanently `stopped` runs would otherwise
    // poll forever, including in a backgrounded tab.
  });

  const sawInFlight = useRef(false);

  useEffect(() => {
    const run = query.data?.run;
    if (run === undefined || run === null) return;

    if (isRunInFlight(run.status)) {
      sawInFlight.current = true;
      return;
    }

    if (sawInFlight.current) {
      sawInFlight.current = false;
      queryClient.invalidateQueries({ queryKey: getKeywordsQueryPrefix(productId) });
    }
  }, [query.data, productId]);

  return query;
}
